import React, { useState } from 'react';
import { 
  Upload, 
  Download, 
  Eye, 
  Calendar, 
  ExternalLink, 
  Check, 
  X, 
  AlertTriangle,
  FileText,
  Trash2
} from 'lucide-react';
import ExpirationWarning from './ExpirationWarning';
import { TechnicianCertification, CertificationUpload } from '../types';

interface CertificationCardProps {
  certification: TechnicianCertification;
  isManager?: boolean;
  canEdit?: boolean;
  onUpload?: (data: CertificationUpload) => void;
  onDelete?: (certId: number) => void;
  onView?: (filePath: string) => void;
  onDownload?: (filePath: string, fileName: string) => void;
  className?: string;
}

const CertificationCard: React.FC<CertificationCardProps> = ({
  certification,
  isManager = false,
  canEdit = false,
  onUpload,
  onDelete,
  onView,
  onDownload,
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadData, setUploadData] = useState({
    obtained_date: certification.obtained_date ? new Date(certification.obtained_date).toISOString().split('T')[0] : '',
    expiration_date: certification.expiration_date ? new Date(certification.expiration_date).toISOString().split('T')[0] : '',
    notes: certification.notes || ''
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const hasFile = Boolean(certification.file_path);
  const isExpired = certification.expiration_date && new Date(certification.expiration_date) < new Date();
  const isVerified = certification.verified_at && certification.is_valid;

  const tierColors = {
    1: 'border-blue-500/30 bg-blue-900/20',
    2: 'border-purple-500/30 bg-purple-900/20',
    3: 'border-yellow-500/30 bg-yellow-900/20'
  };

  const tierColor = tierColors[certification.certification?.tier_level || 1];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a PDF, JPEG, or PNG file.');
        return;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB.');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile || !onUpload) return;

    setIsUploading(true);
    try {
      const uploadPayload: CertificationUpload = {
        certification_id: certification.certification_id,
        file: selectedFile,
        obtained_date: new Date(uploadData.obtained_date),
        expiration_date: uploadData.expiration_date ? new Date(uploadData.expiration_date) : undefined,
        notes: uploadData.notes || undefined
      };

      await onUpload(uploadPayload);
      setShowUploadForm(false);
      setSelectedFile(null);
      setUploadData({ obtained_date: '', expiration_date: '', notes: '' });
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleView = () => {
    if (certification.file_path && onView) {
      onView(certification.file_path);
    }
  };

  const handleDownload = () => {
    if (certification.file_path && certification.file_name && onDownload) {
      onDownload(certification.file_path, certification.file_name);
    }
  };

  return (
    <div className={`bg-gray-800 rounded-lg border ${tierColor} p-4 ${className}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className="text-lg font-semibold text-white">
              {certification.certification?.code 
                ? `${certification.certification.name} ${certification.certification.code}`
                : certification.certification?.name || 'Unknown Certification'}
            </h3>
            {certification.certification?.tier_level && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                certification.certification.tier_level === 1 ? 'bg-blue-600 text-blue-100' :
                certification.certification.tier_level === 2 ? 'bg-purple-600 text-purple-100' :
                'bg-yellow-600 text-yellow-100'
              }`}>
                Tier {certification.certification.tier_level}
              </span>
            )}
          </div>
          
          {certification.certification_number && (
            <p className="text-sm text-gray-400">
              Cert #: {certification.certification_number}
            </p>
          )}

          {/* Status Indicators */}
          <div className="flex items-center space-x-2 mb-3">
            {certification.certification?.upload_required ? (
              // Show upload status only if upload is required
              hasFile ? (
                <div className="flex items-center space-x-1">
                  <Check className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-400">Uploaded</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1">
                  <X className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-red-400">Not Uploaded</span>
                </div>
              )
            ) : (
              // Show completed status for non-upload certifications
              certification.is_valid && (
                <div className="flex items-center space-x-1">
                  <Check className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-400">Completed</span>
                </div>
              )
            )}

            {isVerified && (
              <div className="flex items-center space-x-1">
                <Check className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-blue-400">Verified</span>
              </div>
            )}

            {isExpired && (
              <div className="flex items-center space-x-1">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-400">Expired</span>
              </div>
            )}
          </div>

          {/* Dates */}
          <div className="space-y-1 text-sm text-gray-400">
            {certification.obtained_date && (
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>Obtained: {new Date(certification.obtained_date).toLocaleDateString()}</span>
              </div>
            )}
            {certification.expiration_date && (
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>Expires: {new Date(certification.expiration_date).toLocaleDateString()}</span>
              </div>
            )}
          </div>

          {/* Expiration Warning */}
          {certification.expiration_date && (
            <div className="mt-3">
              <ExpirationWarning 
                expirationDate={new Date(certification.expiration_date)} 
                className="inline-flex"
              />
            </div>
          )}

          {/* Training Link */}
          {certification.certification?.training_link && (
            <div className="mt-3">
              <a
                href={certification.certification.training_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-400 hover:text-blue-300 text-sm"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Training Materials
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center space-x-2 mt-4">
        {certification.certification?.upload_required ? (
          // Show file-related actions only if upload is required
          <>
            {hasFile ? (
              <>
                <button
                  onClick={handleView}
                  className="flex items-center space-x-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-sm transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  <span>View</span>
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center space-x-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-sm transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </button>
              </>
            ) : null}

            {(canEdit || !hasFile) && (
              <button
                onClick={() => setShowUploadForm(true)}
                className="flex items-center space-x-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
              >
                <Upload className="w-4 h-4" />
                <span>{hasFile ? 'Replace' : 'Upload'}</span>
              </button>
            )}

            {isManager && canEdit && hasFile && onDelete && (
              <button
                onClick={() => onDelete(certification.id)}
                className="flex items-center space-x-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            )}
          </>
        ) : (
          // For non-upload certifications, show a simple status
          <div className="text-sm text-gray-400">
            {certification.is_valid ? 
              'This certification does not require a file upload' : 
              'Certification not yet completed'
            }
          </div>
        )}
      </div>

      {/* Upload Form Modal */}
      {showUploadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              Upload Certification
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  File (PDF, JPEG, PNG)
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
                {selectedFile && (
                  <p className="text-sm text-gray-400 mt-1">
                    Selected: {selectedFile.name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Date Obtained
                </label>
                <input
                  type="date"
                  value={uploadData.obtained_date}
                  onChange={(e) => setUploadData(prev => ({ ...prev, obtained_date: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Expiration Date (Optional)
                </label>
                <input
                  type="date"
                  value={uploadData.expiration_date}
                  onChange={(e) => setUploadData(prev => ({ ...prev, expiration_date: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={uploadData.notes}
                  onChange={(e) => setUploadData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  placeholder="Additional notes about this certification..."
                />
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-6">
              <button
                onClick={handleUploadSubmit}
                disabled={!selectedFile || !uploadData.obtained_date || isUploading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white py-2 px-4 rounded transition-colors"
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </button>
              <button
                onClick={() => {
                  setShowUploadForm(false);
                  setSelectedFile(null);
                }}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificationCard;
